import type { GuildWarCandidatePoolMember } from "@/core/guild-war/types"
import type { GuildWarRosterBoard } from "@/lib/guild-war-api"

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  getGuildWarRosterTeamKeys,
  hasGuildWarViceCommanderSlot,
  isGuildWarFlatTeamRoster,
} from "@/core/guild-war/roster-board"
import {
  GUILD_WAR_TEAM_DISPLAY,
  type GuildWarRosterTeamKey,
} from "@/core/guild-war/types"
import { memberDisplayLabel } from "@/lib/member-display-label"

export function RosterViewerAssignmentCard({
  viewerSummary,
}: {
  viewerSummary: { details: string[] } | null
}) {
  return (
    <Card className="overflow-hidden border-border/60 bg-card/70">
      <CardHeader>
        <CardTitle className="text-xl">Your Assignment Summary</CardTitle>
      </CardHeader>
      <CardContent>
        {viewerSummary?.details.length ? (
          <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
            {viewerSummary.details.map((detail) => (
              <li key={detail} className="truncate">
                {detail}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">
            If this looks incorrect, contact an admin to verify the published
            roster snapshot.
          </p>
        )}
      </CardContent>
    </Card>
  )
}

type TeamKey = GuildWarRosterTeamKey
type TeamRole =
  | { kind: "leader"; team: TeamKey }
  | { kind: "member"; team: TeamKey }
  | { kind: "vice"; team: TeamKey }
  | { kind: "subteam"; team: TeamKey; squad: "alpha" | "beta" }

export function buildViewerRosterSummary(input: {
  viewerId: string
  board: GuildWarRosterBoard
  candidateById: Map<string, GuildWarCandidatePoolMember>
}) {
  const viewerRole = findViewerTeamRole(input.board, input.viewerId)
  const commanderSlots = input.board.commanders
    .map((memberId, index) => ({ memberId, index }))
    .filter((entry) => entry.memberId === input.viewerId)
    .map((entry) => ordinal(entry.index + 1))
  const halftimeSlots = input.board.halftimePerformers
    .map((memberId, index) => ({ memberId, index }))
    .filter((entry) => entry.memberId === input.viewerId)
    .map((entry) => ordinal(entry.index + 1))

  const details: string[] = []

  if (viewerRole?.kind === "member" || viewerRole?.kind === "subteam") {
    const teamLabel = toTeamLabel(viewerRole.team)
    const team = input.board[viewerRole.team]!
    const leader = memberLabel(
      team.commander,
      input.candidateById,
      input.board.memberDisplayNames,
    )
    const viceLeader = hasGuildWarViceCommanderSlot(team)
      ? memberLabel(
          team.viceCommander,
          input.candidateById,
          input.board.memberDisplayNames,
        )
      : null

    details.push(
      viewerRole.kind === "member"
        ? `You are a member of ${teamLabel} ${formatLeadersClause(leader, viceLeader)}.`
        : `You are a member of ${teamLabel} Squad ${viewerRole.squad === "alpha" ? "A" : "B"} ${formatLeadersClause(leader, viceLeader)}.`,
    )
  }

  if (viewerRole?.kind === "leader") {
    details.push(`You are the Team Leader of ${toTeamLabel(viewerRole.team)}.`)
  }

  if (viewerRole?.kind === "vice") {
    const team = input.board[viewerRole.team]!
    const leader = memberLabel(
      team.commander,
      input.candidateById,
      input.board.memberDisplayNames,
    )
    details.push(
      `You are the Vice Leader of ${toTeamLabel(viewerRole.team)} under ${quoteIfPresent(leader)}.`,
    )
  }

  if (commanderSlots.length) {
    details.push(
      `You are assigned as commander in slot${commanderSlots.length > 1 ? "s" : ""}: ${commanderSlots.join(", ")}.`,
    )
  }

  if (halftimeSlots.length) {
    details.push(
      `You are assigned as halftime performer in slot${halftimeSlots.length > 1 ? "s" : ""}: ${halftimeSlots.join(", ")}.`,
    )
  }

  return {
    details,
  }
}

export function findViewerTeamRole(
  board: GuildWarRosterBoard,
  viewerId: string,
): TeamRole | null {
  const teams = getGuildWarRosterTeamKeys(board)

  for (const teamKey of teams) {
    const team = board[teamKey]
    if (!team) continue

    if (team.commander === viewerId) {
      return { kind: "leader", team: teamKey }
    }

    if (hasGuildWarViceCommanderSlot(team) && team.viceCommander === viewerId) {
      return { kind: "vice", team: teamKey }
    }

    if (isGuildWarFlatTeamRoster(team)) {
      if (team.members.includes(viewerId)) {
        return { kind: "member", team: teamKey }
      }
    } else {
      for (const subteam of team.subteams) {
        if (subteam.members.includes(viewerId)) {
          return {
            kind: "subteam",
            team: teamKey,
            squad: subteam.key,
          }
        }
      }
    }
  }

  return null
}

function toTeamLabel(team: TeamKey) {
  return GUILD_WAR_TEAM_DISPLAY[team].title
}

function memberLabel(
  memberId: string | null,
  candidateById: Map<string, GuildWarCandidatePoolMember>,
  memberDisplayNames: Record<string, string>,
) {
  if (!memberId) {
    return "Vacant"
  }

  const candidate = candidateById.get(memberId)
  return memberDisplayLabel({
    displayName: memberDisplayNames[memberId] ?? candidate?.guildDisplayName,
  })
}

function quoteIfPresent(value: string) {
  return value === "Vacant" ? "an unassigned Team Leader" : `"${value}"`
}

function formatLeadersClause(leader: string, viceLeader: string | null) {
  if (viceLeader === null) {
    return leader === "Vacant"
      ? "with the leadership slot currently vacant"
      : `under ${`"${leader}"`}`
  }

  if (leader === "Vacant" && viceLeader === "Vacant") {
    return "with both leadership slots currently vacant"
  }

  if (leader === "Vacant") {
    return `under an unassigned Team Leader and ${`"${viceLeader}"`}`
  }

  if (viceLeader === "Vacant") {
    return `under ${`"${leader}"`} and an unassigned Vice Leader`
  }

  return `under ${`"${leader}"`} and ${`"${viceLeader}"`}`
}

function ordinal(value: number) {
  if (value % 100 >= 11 && value % 100 <= 13) {
    return `${value}th`
  }

  const suffix =
    value % 10 === 1
      ? "st"
      : value % 10 === 2
        ? "nd"
        : value % 10 === 3
          ? "rd"
          : "th"
  return `${value}${suffix}`
}
